import { ReactiveVar } from '@apollo/client';
import first from 'lodash/first';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

import type { StrictTypedTypePolicies as AdminStrictTypedTypePolicies } from '../schema/admin/clientHelpers';
import { FlatDrawer as AdminFlatDrawer } from '../schema/admin/drawerModels';
import type { StrictTypedTypePolicies as UserStrictTypedTypePolicies } from '../schema/user/clientHelpers';
import { FlatDrawer as UserFlatDrawer } from '../schema/user/drawerModels';
import { isLiteral, type Literal } from '../services/guards';
import type { NonMutable } from '../services/object';

export const typesafePolicies = <TypePolicies>() => <CustomTypePolicies extends TypePolicies>(
  arg: CustomTypePolicies,
): CustomTypePolicies => arg;

export const isVariables = <T extends Record<string, unknown>>(
  arg: unknown, key: keyof T,
): arg is T => Boolean(arg)
  && isObject(arg)
  && key in arg;

export type ReactiveVars = Readonly<{
  drawersVar: ReactiveVar<(AdminFlatDrawer | UserFlatDrawer)[]>;
}>;

export const idAtKeyFields = (...keyFieldArgs: unknown[]): ('at' | 'id')[] => {
  // types are wrong, docs show what arguments will be passed here
  // https://www.apollographql.com/docs/react/caching/cache-configuration/#typepolicy-fields
  const object = first(keyFieldArgs);

  if (isLiteral(object)) {
    return 'at' in object && !isNil(object.at) ? ['id', 'at'] : ['id'];
  }

  return ['id'];
};

type KeyField<T> = Exclude<keyof T, '__typename'> & string;

export type FlatKeySpecifier<T> = (KeyField<T> | FlatKeySpecifier<T>)[];

type ValidKeySpecifier<T, Keys> =
  Keys extends readonly [infer Key extends KeyField<T>, infer Nested extends NonMutable<unknown[]>, ...infer Rest]
    ? NonNullable<T[Key]> extends Literal
      ? readonly [Key, ValidKeySpecifier<NonNullable<T[Key]>, Nested>, ...ValidKeySpecifier<T, Rest>]
      : readonly [Key, ...ValidKeySpecifier<T, [Nested, ...Rest]>]
    : Keys extends readonly [infer Key extends KeyField<T>, ...infer Rest]
      ? readonly [Key, ...ValidKeySpecifier<T, Rest>]
      : Keys extends readonly []
        ? readonly []
        : readonly [KeyField<T>, ...unknown[]];

export const nestedKeySpecifier = <T extends Literal>() => <const Keys extends NonMutable<unknown[]>>(
  keys: Keys & ValidKeySpecifier<T, Keys>,
  // apollo's types don't handle nested key specifiers they have in their docs
  // https://www.apollographql.com/docs/react/caching/cache-configuration/
): FlatKeySpecifier<T> => keys as unknown as FlatKeySpecifier<T>;

export const sharedPolicies = typesafePolicies<AdminStrictTypedTypePolicies & UserStrictTypedTypePolicies>()({
  GoogleSpreadsheetUrl: {
    keyFields: ['url'],
  },
  GoogleDocUrl: {
    keyFields: ['url'],
  },
  ExternallyHostedFile: {
    keyFields: ['uri'],
  },
  DownloadableFileUrl: {
    keyFields: ['url'],
  },
});

